import {v4 as uuidv4} from 'uuid';
import {requestApi} from '@/utils/httpClient';
import {buildQueryString} from '@/utils';

/**
 * 获取登录URL - 根据当前环境自动判断
 * 开发环境(localhost/dev域名) -> 测试登录
 * 生产环境 -> 生产登录
 */
export function getLoginUrl(): string {
    const hostname = window.location.hostname;
    const searchParams = new URLSearchParams(window.location.search);
    const template_id = searchParams.get('template_id');
    const invitecode = searchParams.get('invitecode');
    const isDev = hostname === 'localhost' || hostname.includes('-dev.');
    let url: string;

    if (isDev) {
        url = 'https://qasandbox.bcetest.baidu.com/api/iam/oauth2/connect?client_id=395029342afa4ed596d8d6475e237222&response_type=code&scope=user_info&redirect_uri=http://famou-dev.now.baidu-int.com/proxyApi/console/v1/oauth/iam/oauthback&state=fmlogin_ws';
    } else {
        url = 'https://console.bce.baidu.com/api/iam/oauth2/connect?client_id=eefac24d01204d8987176d261e5ea760&response_type=code&scope=user_info&redirect_uri=https://pro-service.famou.com/console/v1/oauth/iam/oauthback&state=fm_login_state_ws';
    }

    if (template_id) {
        url += `_templateid_${encodeURIComponent(template_id)}`;
    }

    if (invitecode) {
        url += `_invitecode_${encodeURIComponent(invitecode)}`;
    }

    return url;
}

export async function getApiKeyInfo(): Promise<ApiResponse> {
    return requestApi
        .get('console/v1/users/apikey', {});
}

export async function putApiKeyInfo(): Promise<ApiResponse> {
    return requestApi
        .post('console/v1/users/apikey', {});
}

export async function getUserInfo(params?: {invitecode?: string}): Promise<ApiResponse> {
    const urlParams = buildQueryString(params);
    return requestApi
        .get(`console/v1/users/userinfo${urlParams}`);
}

/** userinfo 里的 wa 用户资料，V2.5 升级弹窗的状态也放在这里 */
export interface WaUserInfo {
    user_name?: string;
    phone?: string;
    email?: string;
    company?: string;
    /** 行业类型 */
    industry?: string;
    /** 工作角色 */
    job_role?: string;
    /** 是否命中 V2.5 升级弹窗名单（Pro 老用户），上线时由运维刷入，接口不可写 */
    v25_target?: boolean;
    /** 升级确认三态：null 没做过选择 / false 点过「暂不升级」并留了手机号 / true 已确认升级 */
    v25_agreed?: boolean | null;
}

export interface UpdateUserInfoParams {
    phone?: string;
    email?: string;
    company?: string;
    /** 行业类型 */
    industry?: string;
    /** 工作角色 */
    job_role?: string;
    /** true=点「立即体验」确认升级；false=点「暂不升级」并提交手机号；不传则不改动升级状态 */
    v25_agreed?: boolean;
    /** 提交来源，仅 v25_agreed=false 时写入 */
    v25_contact_source?: 'webagent' | 'workspace';
}

/** 更新个人资料，同时承载 V2.5 升级弹窗的选择结果 */
export async function updateUserInfo(params: UpdateUserInfoParams): Promise<ApiResponse> {
    return requestApi.post('console/v1/users/info', params);
}

export async function getUserSSO(params?: object): Promise<ApiResponse> {
    const urlParams = buildQueryString(params);
    return requestApi
        .get(`console/v1/users/sso${urlParams}`);
}


export async function logout(): Promise<ApiResponse> {
    return requestApi
        .get('console/v1/oauth/iam/loginout', {});
}

export async function getIsInitial(params): Promise<ApiResponse> {
    const pathParams = params.template_id ? `?template_id=${params.template_id}` : '';

    return requestApi.post(`console/v1/jobs/initial${pathParams}`, {});
}

/**
 * 标记引导步骤已完成
 * @param step 步骤编号: 1=原始算法步骤, 2=评估资源步骤, 3=任务运行页(TODO: 后端字段待定)
 */
export async function finishStep(step: 1 | 2 | 3): Promise<ApiResponse> {
    return requestApi.get('console/v1/users/finish_step', {step});
}

/** SSO ticket 接口所在域名，可能继续用旧域名，也可能改回去，与「新平台地址」的域名判断分开维护 */
export function getSsoTicketHost(): string {
    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' || hostname.includes('-dev.');
    return isDev ? 'https://famou-agent-dev.now.baidu-int.com' : 'https://www.famou.com';
}

/** 新平台地址：跳转类场景统一用这个，线上固定 www.famou.com */
export function getSSOHost(): string {
    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' || hostname.includes('-dev.');
    return isDev ? 'https://famou-agent-dev.now.baidu-int.com' : 'https://www.famou.com';
}

export async function getSsoTicket(params?: object): Promise<ApiResponse> {
    const urlParams = buildQueryString(params);
    const res = await fetch(
        `${getSsoTicketHost()}/proxyApi/console/v1/oauth/sso/ticket${urlParams}`,
        {
            credentials: 'include',
            headers: {
                'x-bce-request-id': uuidv4()
            }
        }
    );
    return res.json();
}

export async function ssoSignByTicket(ticket: string): Promise<ApiResponse> {
    const query = buildQueryString({ticket});
    return requestApi.post(`console/v1/oauth/sso/sign${query}`, {});
}

export async function checkIsLogin(): Promise<ApiResponse> {
    return requestApi.get('console/v1/oauth/is_login', {});
}
